import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Silence benign ResizeObserver loop warnings that trigger CRA's dev error
// overlay (harmless; caused by shadcn Popover/Dialog resizing on open).
if (typeof window !== 'undefined') {
  const swallow = (msg) =>
    typeof msg === 'string' && msg.includes('ResizeObserver loop');
  window.addEventListener('error', (e) => {
    if (swallow(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (swallow(e.reason?.message)) e.preventDefault();
  });
}
import { Toaster } from './components/ui/sonner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { OfflineSyncManager } from './components/OfflineSyncManager';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { FleetPage } from './pages/FleetPage';
import { EnquiriesPage } from './pages/EnquiriesPage';
import { QuotationsPage } from './pages/QuotationsPage';
import { ContractsPage } from './pages/ContractsPage';
import { TimesheetsPage } from './pages/TimesheetsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { CommissionsPage } from './pages/CommissionsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DamageReportsPage } from './pages/DamageReportsPage';
import { FollowUpsPage } from './pages/FollowUpsPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { PurchaseOrdersPage } from './pages/PurchaseOrdersPage';
import { DeliveryNotesPage } from './pages/DeliveryNotesPage';
import { FinancePage } from './pages/FinancePage';
import { EmailParserPage } from './pages/EmailParserPage';
import { AccountingCorePage } from './pages/AccountingCorePage';
import { AssetManagementPage } from './pages/AssetManagementPage';
import { RevenueCostPage } from './pages/RevenueCostPage';
import { FinancialDashboardPage } from './pages/FinancialDashboardPage';
import { GovernancePage } from './pages/GovernancePage';
import { BankReconciliationPage } from './pages/BankReconciliationPage';
import { ClientPOPage } from './pages/ClientPOPage';
import { DeliveryNotesV2Page } from './pages/DeliveryNotesV2Page';
import { EquipmentManpowerPage } from './pages/EquipmentManpowerPage';
import { MaterialModulePage } from './pages/MaterialModulePage';
import { TransportationPage } from './pages/TransportationPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import { OCRExtractionPage } from './pages/OCRExtractionPage';
import { SupplierTimesheetsPage } from './pages/SupplierTimesheetsPage';
import { InvestorPoolsPage } from './pages/InvestorPoolsPage';
import { InvestorLoginPage } from './pages/InvestorLoginPage';
import InvestorPortalPage from './pages/InvestorPortalPage';
import { MobileDeliveryPage } from './pages/MobileDeliveryPage';
import { DemobilizationPage } from './pages/DemobilizationPage';
import { SalesDemobRequestsPage } from './pages/SalesDemobRequestsPage';
import { UsersManagementPage } from './pages/UsersManagementPage';
import { PettyCashPage } from './pages/PettyCashPage';
import { JobAssignmentsPage } from './pages/JobAssignmentsPage';
import { SalesDashboardPage } from './pages/SalesDashboardPage';
import { DriversPage } from './pages/DriversPage';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const PlaceholderPage = ({ title }) => (
  <div className="space-y-6">
    <h1 className="text-4xl font-black tracking-tight">{title}</h1>
    <div className="bg-white rounded-lg border p-12 text-center">
      <p className="text-muted-foreground">This module is under development</p>
    </div>
  </div>
);

function AppContent() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Investor Portal Routes (separate from admin) */}
      <Route path="/investor-login" element={<InvestorLoginPage />} />
      <Route path="/investor-portal" element={<InvestorPortalPage />} />
      {/* Mobile Routes (for drivers/field staff) */}
      <Route path="/mobile/delivery" element={<PrivateRoute><MobileDeliveryPage /></PrivateRoute>} />
      <Route path="/mobile/delivery/:dnId" element={<PrivateRoute><MobileDeliveryPage /></PrivateRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <MyTasksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <AnalyticsDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <PrivateRoute>
            <CompaniesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/fleet"
        element={
          <PrivateRoute>
            <FleetPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/investor-pools"
        element={
          <PrivateRoute>
            <InvestorPoolsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <PrivateRoute>
            <SuppliersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <PrivateRoute>
            <PurchaseOrdersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/delivery-notes"
        element={
          <PrivateRoute>
            <DeliveryNotesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/drivers"
        element={
          <PrivateRoute>
            <DriversPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/enquiries"
        element={
          <PrivateRoute>
            <EnquiriesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/quotations"
        element={
          <PrivateRoute>
            <QuotationsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <PrivateRoute>
            <ContractsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <PrivateRoute>
            <TimesheetsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <InvoicesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <PrivateRoute>
            <FinancePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <PrivateRoute>
            <CommissionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <DocumentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <DamageReportsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/follow-ups"
        element={
          <PrivateRoute>
            <FollowUpsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/email-parser"
        element={
          <PrivateRoute>
            <EmailParserPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/accounting"
        element={
          <PrivateRoute>
            <AccountingCorePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/asset-management"
        element={
          <PrivateRoute>
            <AssetManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/revenue-cost"
        element={
          <PrivateRoute>
            <RevenueCostPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/financial-dashboard"
        element={
          <PrivateRoute>
            <FinancialDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/governance"
        element={
          <PrivateRoute>
            <GovernancePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/bank-reconciliation"
        element={
          <PrivateRoute>
            <BankReconciliationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/client-pos"
        element={
          <PrivateRoute>
            <ClientPOPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/delivery-notes-v2"
        element={
          <PrivateRoute>
            <DeliveryNotesV2Page />
          </PrivateRoute>
        }
      />
      <Route
        path="/demobilization"
        element={
          <PrivateRoute>
            <DemobilizationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/demob-requests"
        element={
          <PrivateRoute>
            <SalesDemobRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/equipment-manpower"
        element={
          <PrivateRoute>
            <EquipmentManpowerPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/material-module"
        element={
          <PrivateRoute>
            <MaterialModulePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/transportation"
        element={
          <PrivateRoute>
            <TransportationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/collections"
        element={
          <PrivateRoute>
            <CollectionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-extraction"
        element={
          <PrivateRoute>
            <OCRExtractionPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/supplier-timesheets"
        element={
          <PrivateRoute>
            <SupplierTimesheetsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <UsersManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/petty-cash"
        element={
          <PrivateRoute>
            <PettyCashPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/job-assignments"
        element={
          <PrivateRoute>
            <JobAssignmentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-sales"
        element={
          <PrivateRoute>
            <SalesDashboardPage />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
        <Toaster position="top-right" />
        <PWAInstallPrompt />
        <OfflineSyncManager />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
